import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { LabelWithChildren } from '@/components/ui/label-with-children';
import { Loader2 } from 'lucide-react';
import { technicalAnalysisService, TechnicalAnalysisData } from '@/services/technicalAnalysisService';

interface TechAnalysisPanelProps {
  symbol: string;
  data?: any;
}

const TechAnalysisPanel: React.FC<TechAnalysisPanelProps> = ({ symbol, data }) => {
  const [analysisData, setAnalysisData] = useState<TechnicalAnalysisData | null>(null);
  const [timeFrame, setTimeFrame] = useState<string>('1D');
  const [indicatorType, setIndicatorType] = useState<string>('Momentum');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAnalysisData = async () => {
      if (!symbol) return;
      
      setLoading(true);
      setError(null);
      try {
        const data = await technicalAnalysisService.getTechnicalAnalysis(symbol, timeFrame);
        setAnalysisData(data);
      } catch (err: any) {
        console.error('Error fetching technical analysis:', err);
        setError(err.message || 'Failed to load technical analysis data');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalysisData();
  }, [symbol, timeFrame]);

  const handleTimeFrameClick = (frame: string) => {
    setTimeFrame(frame);
  };

  const handleIndicatorTypeClick = (type: string) => {
    setIndicatorType(type);
  };

  return (
    <Card className="w-full h-full bg-white shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold">{symbol} Technical Analysis</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <LabelWithChildren className="text-sm font-medium">
                Time Frame
              </LabelWithChildren>
              <div className="flex mt-1 space-x-2">
                <button 
                  className={`px-3 py-1 text-xs ${timeFrame === '1D' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'} rounded-full`}
                  onClick={() => handleTimeFrameClick('1D')}
                >1D</button>
                <button 
                  className={`px-3 py-1 text-xs ${timeFrame === '1W' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'} rounded-full`}
                  onClick={() => handleTimeFrameClick('1W')}
                >1W</button>
                <button 
                  className={`px-3 py-1 text-xs ${timeFrame === '1M' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'} rounded-full`}
                  onClick={() => handleTimeFrameClick('1M')}
                >1M</button>
                <button 
                  className={`px-3 py-1 text-xs ${timeFrame === '1Y' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'} rounded-full`}
                  onClick={() => handleTimeFrameClick('1Y')}
                >1Y</button>
              </div>
            </div>
            <div>
              <LabelWithChildren className="text-sm font-medium">
                Indicator Type
              </LabelWithChildren>
              <div className="flex mt-1 space-x-2">
                <button 
                  className={`px-3 py-1 text-xs ${indicatorType === 'Momentum' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'} rounded-full`}
                  onClick={() => handleIndicatorTypeClick('Momentum')}
                >Momentum</button>
                <button 
                  className={`px-3 py-1 text-xs ${indicatorType === 'Trend' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'} rounded-full`}
                  onClick={() => handleIndicatorTypeClick('Trend')}
                >Trend</button>
                <button 
                  className={`px-3 py-1 text-xs ${indicatorType === 'Volatility' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'} rounded-full`}
                  onClick={() => handleIndicatorTypeClick('Volatility')}
                >Volatility</button>
              </div>
            </div>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : error ? (
            <div className="py-4 text-center text-red-500">Error: {error}</div>
          ) : analysisData ? (
            <>
              <div>
                <LabelWithChildren className="text-sm font-medium">
                  Indicators
                </LabelWithChildren>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
                  <div className="p-2 bg-gray-50 rounded border">
                    <div className="text-xs text-gray-500">RSI (14)</div>
                    <div className="text-lg font-semibold">{analysisData.indicators.rsi.value}</div>
                    <div className={`text-xs ${
                      analysisData.indicators.rsi.signal === 'Bullish' ? 'text-green-600' : 
                      analysisData.indicators.rsi.signal === 'Bearish' ? 'text-red-600' : 
                      'text-yellow-600'
                    }`}>{analysisData.indicators.rsi.signal}</div>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <div className="text-xs text-gray-500">MACD</div>
                    <div className="text-lg font-semibold">{analysisData.indicators.macd.value > 0 ? '+' : ''}{analysisData.indicators.macd.value}</div>
                    <div className={`text-xs ${
                      analysisData.indicators.macd.signal === 'Bullish' ? 'text-green-600' : 
                      analysisData.indicators.macd.signal === 'Bearish' ? 'text-red-600' : 
                      'text-yellow-600'
                    }`}>{analysisData.indicators.macd.signal}</div>
                  </div>
                  <div className="p-2 bg-gray-50 rounded border">
                    <div className="text-xs text-gray-500">Stochastic</div>
                    <div className="text-lg font-semibold">{analysisData.indicators.stochastic.value}</div>
                    <div className={`text-xs ${
                      analysisData.indicators.stochastic.signal === 'Bullish' ? 'text-green-600' : 
                      analysisData.indicators.stochastic.signal === 'Bearish' ? 'text-red-600' : 
                      'text-yellow-600'
                    }`}>{analysisData.indicators.stochastic.signal}</div>
                  </div>
                </div>
              </div>
              
              <div>
                <LabelWithChildren className="text-sm font-medium">
                  Chart
                </LabelWithChildren>
                <div className="mt-1 h-64 bg-gray-50 rounded border flex items-center justify-center">
                  {analysisData.chartData ? (
                    <div className="text-gray-400 text-sm">
                      Chart data loaded - visualization to be implemented
                    </div>
                  ) : (
                    <div className="text-gray-400 text-sm">
                      No chart data available for {timeFrame} timeframe
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <LabelWithChildren className="text-sm font-medium">
                  Analysis
                </LabelWithChildren>
                <div className="mt-1 p-3 bg-gray-50 rounded border">
                  <p className="text-sm">
                    {analysisData.analysisText}
                  </p>
                </div>
              </div>
            </>
          ) : (
            <div className="py-4 text-center text-gray-500">No technical analysis data available for {symbol}</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TechAnalysisPanel;